import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { BsWindows, BsDownload, BsShield, BsLightning, BsBook } from "react-icons/bs";
import { FaAppStore, FaApple, FaUbuntu, FaMobile, FaDesktop, FaCheckCircle } from "react-icons/fa";
import { IoLogoGooglePlaystore } from "react-icons/io5";
import { SiHuawei } from "react-icons/si";
import { motion } from "framer-motion";

const mobileAppLinks = [
	{
		name: "Google Play Store",
		icon: <IoLogoGooglePlaystore size={32} />,
		url: "https://play.google.com/store/apps/details?id=com.akellolibrary",
		description: "Download for Android devices",
		available: true,
	},
	{
		name: "App Store",
		icon: <FaAppStore size={32} />,
		url: "https://apps.apple.com/zw/app/akello-library/id6451323375",
		description: "Download for iOS devices",
		available: true,
	},
	{
		name: "Huawei App Gallery",
		icon: <SiHuawei size={32} />,
		url: "https://appgallery.huawei.com/app/C108363007",
		description: "Download for Huawei devices",
		available: true,
	},
];

const desktopAppLinks = [
	{
		name: "Windows",
		icon: <BsWindows size={32} />,
		url:
			process.env.REACT_APP_WINDOWS_APP_LINK ||
			"https://library.akello.co/akello-windowsx64-installation-file.zip",
		description: "Download for Windows 10/11",
		available: true,
	},
	{
		name: "Linux",
		icon: <FaUbuntu size={32} />,
		url: "#",
		description: "Download for Linux systems",
		available: false,
	},
	{
		name: "Mac",
		icon: <FaApple size={32} />,
		url: "#",
		description: "Download for macOS",
		available: false,
	},
];

const features = [
	{
		icon: <BsBook size={24} />,
		title: "Vast Library",
		description: "Access thousands of books, both academic and non-academic",
	},
	{
		icon: <BsLightning size={24} />,
		title: "Fast Reading",
		description: "Optimized for quick loading and smooth reading experience",
	},
	{
		icon: <BsShield size={24} />,
		title: "Secure & Private",
		description: "Your reading data is protected with enterprise-grade security",
	},
	{
		icon: <BsDownload size={24} />,
		title: "Offline Access",
		description: "Download books to read anywhere, even without internet",
	},
];

const Downloads = () => {
	return (
		<Container>
			<PaddingX>
				<div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-transparent">
					{/* Hero Section */}
					{/* <div className="pt-10 pb-6">
						<motion.div
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6 }}
							className="text-center  mx-auto"
						>
							<h1 className="text-2xl md:text-3xl text-gray-700 font-bold mb-3 leading-tight">
								Download Akello Library
								<span className="block text-akgreen">Anywhere, Anytime</span>
							</h1>
						</motion.div>
					</div> */}

					{/* Features Section */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="mb-10 pt-10"
					>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
							{features.map((feature, index) => (
								<motion.div
									key={feature.title}
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
									className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 group"
								>
									<div className="flex items-center justify-center w-12 h-12 bg-akgreen/10 rounded-xl mb-4 group-hover:bg-akgreen/20 transition-colors duration-300">
										<div className="text-akgreen">{feature.icon}</div>
									</div>
									<h3 className="font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
									<p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
								</motion.div>
							))}
						</div>
					</motion.div>

					{/* Download Sections */}
					<div className="max-w-6xl mx-auto">
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
							{/* Mobile Downloads */}
							<motion.div
								initial={{ opacity: 0, x: -30 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.6, delay: 0.4 }}
								className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
							>
								<div className="flex items-center gap-3 mb-6">
									<div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl">
										<FaMobile className="text-blue-600 text-xl" />
									</div>
									<h2 className="text-2xl font-bold text-gray-900">Mobile Apps</h2>
								</div>
								<p className="text-gray-600 mb-6">
									Download our mobile app for iOS, Android, and Huawei devices
								</p>

								<div className="space-y-4">
									{mobileAppLinks.map((link, index) => (
										<motion.a
											key={link.name}
											href={link.url}
											target="_blank"
											rel="noreferrer noopener"
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
											whileHover={{ scale: 1.02, y: -2 }}
											whileTap={{ scale: 0.98 }}
											className="block group"
										>
											<div className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 hover:border-blue-300 hover:shadow-md transition-all duration-300 group-hover:bg-gradient-to-r group-hover:from-blue-100 group-hover:to-indigo-100">
												<div className="flex-shrink-0">{link.icon}</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2 mb-1">
														<span className="text-sm font-medium text-gray-600">Download on</span>
														<FaCheckCircle className="text-green-500 text-sm" />
													</div>
													<h3 className="font-bold text-lg text-gray-900 group-hover:text-blue-700 transition-colors">
														{link.name}
													</h3>
													<p className="text-sm text-gray-500">{link.description}</p>
												</div>
												<div className="flex-shrink-0">
													<BsDownload className="text-gray-400 group-hover:text-blue-600 transition-colors" />
												</div>
											</div>
										</motion.a>
									))}
								</div>
							</motion.div>

							{/* Desktop Downloads */}
							<motion.div
								initial={{ opacity: 0, x: 30 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ duration: 0.6, delay: 0.6 }}
								className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100"
							>
								<div className="flex items-center gap-3 mb-6">
									<div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-xl">
										<FaDesktop className="text-purple-600 text-xl" />
									</div>
									<h2 className="text-2xl font-bold text-gray-900">Desktop Apps</h2>
								</div>
								<p className="text-gray-600 mb-6">
									Get the full desktop experience on Windows, Mac, and Linux
								</p>

								<div className="space-y-4">
									{desktopAppLinks.map((link, index) => (
										<motion.a
											key={link.name}
											href={link.url}
											target={link.url === "#" ? "_self" : "_blank"}
											rel="noreferrer noopener"
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ duration: 0.4, delay: 0.7 + index * 0.1 }}
											whileHover={link.available ? { scale: 1.02, y: -2 } : {}}
											whileTap={link.available ? { scale: 0.98 } : {}}
											className={`block group ${!link.available ? "cursor-not-allowed" : ""}`}
										>
											<div
												className={`flex items-center gap-4 p-4 rounded-2xl border transition-all duration-300 ${
													link.available
														? "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-100 hover:border-purple-300 hover:shadow-md group-hover:bg-gradient-to-r group-hover:from-purple-100 group-hover:to-pink-100"
														: "bg-gray-50 border-gray-200 opacity-60"
												}`}
											>
												<div className="flex-shrink-0">{link.icon}</div>
												<div className="flex-1 min-w-0">
													<div className="flex items-center gap-2 mb-1">
														<span className="text-sm font-medium text-gray-600">Download for</span>
														{link.available ? (
															<FaCheckCircle className="text-green-500 text-sm" />
														) : (
															<div className="w-2 h-2 bg-gray-400 rounded-full"></div>
														)}
													</div>
													<div className="flex items-center gap-2">
														<h3
															className={`font-bold text-lg transition-colors ${
																link.available
																	? "text-gray-900 group-hover:text-purple-700"
																	: "text-gray-500"
															}`}
														>
															{link.name}
														</h3>
														{!link.available && (
															<span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full font-medium">
																Coming Soon
															</span>
														)}
													</div>
													<p
														className={`text-sm ${
															link.available ? "text-gray-500" : "text-gray-400"
														}`}
													>
														{link.description}
													</p>
												</div>
												<div className="flex-shrink-0">
													{link.available ? (
														<BsDownload className="text-gray-400 group-hover:text-purple-600 transition-colors" />
													) : (
														<div className="w-4 h-4 border-2 border-gray-300 rounded-full"></div>
													)}
												</div>
											</div>
										</motion.a>
									))}
								</div>
							</motion.div>
						</div>
					</div>
				</div>
			</PaddingX>
		</Container>
	);
};

export default Downloads;
